import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Target, Users, FileText, CheckCircle, Clock, AlertTriangle, MapPin, Play, Eye, RefreshCw, Download } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';
import { useToast } from '../../../../../../context/ToastContext';
import exportAuditPlanService from '../../../../../../services/audit_services/export_pdf_services/exportAuditPlanService';

// Import modals
import StartAuditModal from './auditPlanModals/StartAuditModal';
import CompleteAuditModal from './auditPlanModals/CompleteAuditModal';
import ReopenAuditModal from './auditPlanModals/ReopenAuditModal';

export default function MyAuditPlanDetails() {
  const { id } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [auditPlan, setAuditPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showStartModal, setShowStartModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchAuditPlanDetails();
  }, [id]);

  const fetchAuditPlanDetails = async () => {
    setLoading(true);
    
    try {
      const result = await auditPlanService.getMyAuditPlanDetail(authTokens.access, id);
      
      if (result.success) {
        setAuditPlan(result.data);
      } else {
        console.error('Error fetching audit plan details:', result.error);
        showError(result.error || 'Error loading audit plan details');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audit plan details:', error);
      showError('Error loading audit plan details');
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Planifié', icon: Clock },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours', icon: Target },
      under_review: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'En révision', icon: FileText },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé', icon: CheckCircle },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Clôturé', icon: CheckCircle }
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-green-100', text: 'text-green-800', label: 'Vous êtes l\'auditeur' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Vous êtes un audité' }
    };

    const config = roleConfig[role] || roleConfig.auditee;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Users className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const getProbabilityBadge = (probability) => {
    const config = {
      'critical': { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' },
      'high': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Élevé' },
      'medium': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Moyen' },
      'low': { bg: 'bg-green-100', text: 'text-green-800', label: 'Faible' },
      // French fallbacks for existing data
      'Critique': { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' },
      'Élevé': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Élevé' },
      'Moyen': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Moyen' },
      'Faible': { bg: 'bg-green-100', text: 'text-green-800', label: 'Faible' }
    };

    const style = config[probability] || config['medium'];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    
    try {
      const result = await exportAuditPlanService.exportAuditPlanPDF(authTokens.access, id);
      
      if (result.success) {
        showSuccess('Plan d\'audit exporté avec succès');
      } else {
        showError(result.error || 'Erreur lors de l\'exportation du plan d\'audit');
      }
    } catch (error) {
      console.error('Error exporting audit plan:', error);
      showError('Erreur lors de l\'exportation du plan d\'audit');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      showSuccess(`Téléchargement de ${fileName}...`);
      const result = await auditPlanService.downloadAttachment(authTokens.access, attachmentId);
      
      if (result.success) {
        showSuccess('Fichier téléchargé avec succès');
      } else {
        showError(result.error || 'Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showError('Erreur lors du téléchargement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails du plan d'audit...</p>
        </div>
      </div>
    );
  }

  if (!auditPlan) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Plan d'audit introuvable ou vous n'y avez pas accès</p>
        <Link to="/dashboard/audit/my-plans" className="text-green-600 hover:underline mt-4 inline-block">
          Retour à mes plans d'audit
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-8">
      {/* Modals */}
      <StartAuditModal
        isOpen={showStartModal}
        onClose={() => setShowStartModal(false)}
        planId={id}
        onSuccess={fetchAuditPlanDetails}
      />
      <CompleteAuditModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        planId={id}
        onSuccess={fetchAuditPlanDetails}
      />
      <ReopenAuditModal
        isOpen={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        planId={id}
        onSuccess={fetchAuditPlanDetails}
      />

      {/* Header Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <Link
                to="/dashboard/audit/my-plans"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{auditPlan.title}</h1>
                <p className="text-sm text-gray-500 mt-2">Détails du plan d'audit</p>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              {getStatusBadge(auditPlan.status)}
              {getRoleBadge(auditPlan.my_role)}
            </div>
          </div>

          {/* Program Link */}
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <Target className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm text-green-900">
                Fait partie de :{' '}
                <Link
                  to={`/dashboard/audit/programs/${auditPlan.program.id}`}
                  className="font-semibold hover:underline"
                >
                  {auditPlan.program.title}
                </Link>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Actions disponibles</h3>
          
          {/* Export Button */}
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Exportation...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Exporter en PDF
              </>
            )}
          </button>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {auditPlan.my_role === 'auditor' && (
            <>
              {/* Not Released Message */}
              {!auditPlan.is_released && (
                <div className="col-span-full mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-orange-600 mr-2" />
                    <span className="text-sm text-orange-800">
                      Ce plan d'audit n'a pas encore été publié. Vous ne pouvez pas commencer à travailler dessus tant qu'il n'est pas publié par le gestionnaire d'audit.
                    </span>
                  </div>
                </div>
              )}

              {auditPlan.is_released && auditPlan.status != "in_progress" && (
              <div className="grid grid-cols-1">
                {/* View Sections Button - Always available for non-auditors */}
                <Link
                  to={`/dashboard/audit/audit-plans/${auditPlan.id}/sections`}
                  className="flex items-center justify-center px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                >
                  <Eye className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Voir les sections</span>
                </Link>
              </div>
              )}

              {/* Start Audit Button - Only show if released and status is pending */}
              {auditPlan.is_released && auditPlan.status === 'pending' && (
                <button
                  onClick={() => setShowStartModal(true)}
                  className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg"
                >
                  <Play className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Démarrer l'audit</span>
                </button>
              )}

              {/* Complete Audit Button - Only show if in progress */}
              {auditPlan.status === 'in_progress' && (
                <button
                  onClick={() => setShowCompleteModal(true)}
                  className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Terminer l'audit</span>
                </button>
              )}

              {/* Reopen Audit Plan Button - Only show if completed */}
              {auditPlan.status === 'completed' && (
                <button
                  onClick={() => setShowReopenModal(true)}
                  className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all shadow-md hover:shadow-lg"
                >
                  <RefreshCw className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Rouvrir le plan d'audit</span>
                </button>
              )}

              {/* Manage Sections Button - Only show if in progress */}
              {auditPlan.status === 'in_progress' && (
                <Link
                  to={`/dashboard/audit/audit-plans/${auditPlan.id}/sections`}
                  className="flex items-center justify-center px-4 py-3 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
                >
                  <Target className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Gérer les sections</span>
                </Link>
              )}

              {/* Generate Report Button - Show for completed audits */}
              {auditPlan.status === 'completed' && (
                <Link
                  to={`/dashboard/audit/reports?audit_plan=${auditPlan.id}`}
                  className="flex items-center justify-center px-4 py-3 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Générer un rapport</span>
                </Link>
              )}
            </>
          )}
        </div>

        {/* Actions for non-auditors (auditees, participants) */}
        {auditPlan.my_role !== 'auditor' && (
          <>
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm text-green-800">
                  Vous participez en tant que <strong>{auditPlan.my_role}</strong> à cet audit. Vous pouvez consulter les sections et fournir des informations si nécessaire.
                </span>
              </div>

              
              {auditPlan.is_released && auditPlan.status != "pending" && (
              <div className="grid grid-cols-1">
                {/* View Sections Button - Always available for non-auditors */}
                <Link
                  to={`/dashboard/audit/audit-plans/${auditPlan.id}/sections`}
                  className="flex items-center justify-center px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                >
                  <Eye className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Voir les sections</span>
                </Link>
              </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Main Content - 3 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Audit Information & Program Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Audit Information */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Informations sur l'audit
            </h3>
            
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Objectifs</p>
                <p className="text-sm text-gray-900 leading-relaxed">{auditPlan.objectives}</p>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Périmètre</p>
                <p className="text-sm text-gray-900 leading-relaxed">{auditPlan.scope}</p>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Normes de référence</p>
                <p className="text-sm text-gray-900 leading-relaxed">{auditPlan.reference}</p>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-1 text-green-600" />
                  Localisation
                </p>
                <p className="text-sm text-gray-900">{auditPlan.location}</p>
              </div>

              {auditPlan.note && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notes</p>
                  <p className="text-sm text-gray-900 leading-relaxed">{auditPlan.note}</p>
                </div>
              )}
            </div>
          </div>

          {/* Audit Program Details */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2 text-green-600" />
              Détails du programme d'audit
            </h3>
            
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Titre du programme</p>
                <p className="text-sm text-gray-900 font-medium">{auditPlan.program.title}</p>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Objectif du programme</p>
                <p className="text-sm text-gray-900 leading-relaxed">{auditPlan.program.objective}</p>
              </div>

              {auditPlan.program.scope && auditPlan.program.scope.length > 0 && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Périmètre du programme</p>
                  <div className="space-y-2">
                    {auditPlan.program.scope.map((scopeItem, index) => (
                      <div key={index} className="flex items-start text-sm text-gray-900">
                        <span className="w-1.5 h-1.5 bg-green-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                        <span className="leading-relaxed">{scopeItem}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Identified Risks */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                Risques identifiés ({auditPlan.risks.length})
              </h3>
            </div>

            {auditPlan.risks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Risque
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Probabilité
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditPlan.risks.map((risk) => (
                      <tr key={risk.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{risk.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{risk.description}</span>
                        </td>
                        <td className="px-4 py-3">
                          {getProbabilityBadge(risk.probability)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucun risque identifié</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* All Parties Involved */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
              <Users className="h-4 w-4 mr-2 text-green-600" />
              Toutes les parties impliquées ({auditPlan.parties.length})
            </h3>
            <div className="space-y-3">
              {auditPlan.parties.map((party) => (
                <div key={party.id} className="p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-green-500 to-green-600 flex-shrink-0">
                        <span className="text-sm font-semibold text-white">
                          {party.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {party.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{party.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="ml-13">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      party.partyRole === 'auditor' ? 'bg-green-100 text-green-800' :
                      party.partyRole === 'auditee' ? 'bg-green-100 text-green-800' :
                      party.partyRole === 'participant' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {party.partyRole}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Attachments */}
          {auditPlan.attachments && auditPlan.attachments.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-green-600" />
                Pièces jointes
              </h3>
              <div className="space-y-3">
                {auditPlan.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {attachment.file_size && `${(attachment.file_size / 1024).toFixed(2)} KB • `}
                          Téléchargé le {new Date(attachment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                      className="inline-flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Métadonnées</h3>
            <div className="space-y-4">
              <div className="pb-4 border-b border-gray-100">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Créé par</p>
                <p className="text-sm font-medium text-gray-900">{auditPlan.created_by}</p>
              </div>
              <div className="pb-4 border-b border-gray-100">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date de création</p>
                <p className="text-sm font-medium text-gray-900">{new Date(auditPlan.created_at).toLocaleDateString('fr-FR')}</p>
              </div>

              {/* Release Date – only show if it exists */}
              {auditPlan.release_date && (
                <div className="pb-4 border-b border-gray-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Date de publication
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(auditPlan.release_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}

              {/* Publish Date – only show if it exists */}
              {auditPlan.publish_date && (
                <div className="pb-4 border-b border-gray-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Date de publication
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(auditPlan.publish_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dernière mise à jour</p>
                <p className="text-sm font-medium text-gray-900">{new Date(auditPlan.updated_at).toLocaleDateString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dernière mise à jour par</p>
                <p className="text-sm font-medium text-gray-900">{auditPlan.last_updated_by}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}